import type { AttachmentLink, DocumentBlock, DocumentKind, DocumentLink, DocumentLinkTargetKind, DocumentVisibility, PlanDocument, TaskComment } from "./types";

type Inline =
  | { type: "text"; text: string; styles: Record<string, never> }
  | { type: "mention"; props: { targetKind: DocumentLinkTargetKind; targetId: string } };

const THUMB =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180"><rect width="320" height="180" fill="#f3f4f6"/><rect x="20" y="24" width="280" height="132" rx="6" fill="#e5e7eb"/></svg>`,
  );

function block(id: string, type: string, content: Inline[] | string, props?: Record<string, unknown>): DocumentBlock {
  const inline: Inline[] = typeof content === "string" ? [{ type: "text", text: content, styles: {} }] : content;
  return { id, type, ...(props ? { props } : {}), content: inline };
}

export function heading(id: string, text: string): DocumentBlock {
  return block(id, "heading", text, { level: 2 });
}

export function paragraph(id: string, text: string): DocumentBlock {
  return block(id, "paragraph", text);
}

export function paragraphMention(id: string, before: string, kind: DocumentLinkTargetKind, targetId: string, after: string): DocumentBlock {
  return block(id, "paragraph", [
    { type: "text", text: before, styles: {} },
    { type: "mention", props: { targetKind: kind, targetId } },
    { type: "text", text: after, styles: {} },
  ]);
}

export function bullet(id: string, text: string): DocumentBlock {
  return block(id, "bulletListItem", text);
}

export function quoteBlock(id: string, text: string): DocumentBlock {
  return block(id, "quote", text);
}

export function checkItem(id: string, text: string, checked: boolean): DocumentBlock {
  return block(id, "checkListItem", text, { checked });
}

function inlineText(content: unknown): string {
  if (!Array.isArray(content)) return "";
  const parts: string[] = [];
  for (const item of content) {
    if (!item || typeof item !== "object") continue;
    const row = item as { type?: string; text?: string; content?: unknown };
    if (row.type === "mention") continue;
    if (typeof row.text === "string") parts.push(row.text);
    if (row.content) parts.push(inlineText(row.content));
  }
  return parts.join("");
}

function plain(blocks: DocumentBlock[]): string {
  return blocks
    .map((item) => inlineText(item.content))
    .filter((line) => line.length > 0)
    .join("\n");
}

export function sampleDoc(input: {
  id: string;
  seq: number;
  title: string;
  kind: DocumentKind;
  visibility: DocumentVisibility;
  authorId: string;
  blocks: DocumentBlock[];
  createdAt: string;
  updatedAt?: string;
  heldAt?: string;
  deletedAt?: string;
  attachments?: AttachmentLink[];
  comments?: TaskComment[];
}): PlanDocument {
  return {
    id: input.id,
    seq: input.seq,
    title: input.title,
    kind: input.kind,
    visibility: input.visibility,
    authorId: input.authorId,
    blocks: input.blocks,
    plainText: plain(input.blocks),
    attachments: input.attachments ?? [],
    comments: input.comments ?? [],
    activity: [{ id: `${input.id}_ac`, at: input.createdAt, actor: input.authorId, message: "문서를 만들었습니다" }],
    createdAt: input.createdAt,
    updatedAt: input.updatedAt ?? input.createdAt,
    ...(input.heldAt ? { heldAt: input.heldAt } : {}),
    ...(input.deletedAt ? { deletedAt: input.deletedAt } : {}),
  };
}

export function docLink(input: {
  id: string;
  documentId: string;
  targetKind: DocumentLinkTargetKind;
  targetId: string;
  origins: DocumentLink["origins"];
  title: string;
  code?: string;
  createdAt: string;
  createdBy: string;
  blockId?: string;
}): DocumentLink {
  return {
    id: input.id,
    documentId: input.documentId,
    targetKind: input.targetKind,
    targetId: input.targetId,
    origins: input.origins,
    snapshot: { ...(input.code ? { code: input.code } : {}), title: input.title },
    createdAt: input.createdAt,
    createdBy: input.createdBy,
    ...(input.blockId ? { blockId: input.blockId } : {}),
  };
}

export function fileAttachment(id: string, title: string, at: string, addedBy: string, size = 184320): AttachmentLink {
  return { id, title, url: `https://example.com/files/${encodeURIComponent(title)}`, addedAt: at, addedBy, kind: "file", size, mimeType: "application/pdf" };
}

export function linkAttachment(id: string, title: string, url: string, at: string, addedBy: string): AttachmentLink {
  return { id, title, url, addedAt: at, addedBy, kind: "link" };
}

export function imageAttachment(id: string, title: string, at: string, addedBy: string): AttachmentLink {
  return { id, title, url: THUMB, addedAt: at, addedBy, kind: "image", size: 2480, mimeType: "image/svg+xml" };
}

export function sampleErpDocuments(): PlanDocument[] {
  return [
    sampleDoc({
      id: "erp_doc_kickoff",
      seq: 1,
      title: "제조 ERP 킥오프",
      kind: "meeting",
      visibility: "plan",
      authorId: "김PM",
      heldAt: "2026-09-02",
      createdAt: "2026-09-02T01:00:00.000Z",
      updatedAt: "2026-09-03T04:00:00.000Z",
      blocks: [
        heading("erp_b1", "참석과 범위"),
        paragraph("erp_b2", "생산·구매·재무 리드와 PMO가 9월 착수 범위와 3월 오픈 가정을 맞췄습니다."),
        bullet("erp_b3", "1차는 생산·구매·재고·재무이고, 현장 모바일은 별도 결정으로 미룹니다."),
        bullet("erp_b4", "MES 연동 사양은 10월 설계 전에 버전을 잠급니다."),
        paragraphMention("erp_b5", "헌장과 참석자 명단을 배포한다 ", "executionTask", "x_kickoff_minutes", ""),
        checkItem("erp_b6", "프로젝트룸 방화벽 개방을 인프라에 요청한다", false),
      ],
      attachments: [fileAttachment("erp_doc_att_kickoff", "킥오프 참석자 명단.pdf", "2026-09-02T02:00:00.000Z", "김PM")],
      comments: [{ id: "erp_doc_cm1", author: "최기획", body: "인터뷰 일정은 요구 분석 첫 주에 넣었습니다.", at: "2026-09-03T04:00:00.000Z" }],
    }),
    sampleDoc({
      id: "erp_doc_weekly",
      seq: 2,
      title: "10월 3주차 주간 보고 초안",
      kind: "memo",
      visibility: "plan",
      authorId: "나",
      createdAt: "2026-10-20T01:00:00.000Z",
      updatedAt: "2026-10-20T06:00:00.000Z",
      blocks: [
        heading("erp_w1", "이번 주"),
        paragraphMention("erp_w2", "진척 숫자는 ", "executionTask", "x_weekly", "에 모으고, 본문에는 밀린 이유만 적습니다."),
        bullet("erp_w3", "TO-BE는 80%이고, GAP 목록은 40%에서 현업 리뷰를 기다립니다."),
        bullet("erp_w4", "MES 사양 버전이 어긋나 인터페이스 설계가 하루 밀렸습니다."),
        quoteBlock("erp_w5", "오픈 가정 2027-03-31은 유지합니다. 방화벽이 이번 주를 넘기면 개발 환경만 다시 봅니다."),
      ],
      attachments: [linkAttachment("erp_doc_att_weekly", "현황 초안", "https://example.com/reports/erp-w43", "2026-10-20T01:30:00.000Z", "나")],
    }),
    sampleDoc({
      id: "erp_doc_if",
      seq: 3,
      title: "MES 인터페이스 필드 메모",
      kind: "material",
      visibility: "private",
      authorId: "나",
      createdAt: "2026-10-18T08:00:00.000Z",
      blocks: [
        paragraph("erp_i1", "레거시 WMS와 맞출 때 실적 코드, 창고, 로트 세 칸이 아직 이름이 다릅니다. 매핑 표는 박엔진 작업에 붙입니다."),
        bullet("erp_i2", "실적 코드는 생산 모듈 기준을 따르고, 구 코드는 변환 테이블로 남깁니다."),
      ],
      attachments: [imageAttachment("erp_doc_att_if", "필드 매핑 스케치.png", "2026-10-18T08:10:00.000Z", "나")],
    }),
    sampleDoc({
      id: "erp_doc_old",
      seq: 4,
      title: "착수 전 일정 메모",
      kind: "memo",
      visibility: "plan",
      authorId: "김PM",
      createdAt: "2026-09-01T02:00:00.000Z",
      updatedAt: "2026-10-18T00:00:00.000Z",
      deletedAt: "2026-10-18T00:00:00.000Z",
      blocks: [paragraph("erp_old1", "오픈을 2월로 적었던 초안입니다. 킥오프에서 3월로 고쳤습니다.")],
    }),
  ];
}

export function sampleErpDocumentLinks(): DocumentLink[] {
  return [
    docLink({
      id: "erp_dl_1",
      documentId: "erp_doc_kickoff",
      targetKind: "executionTask",
      targetId: "x_kickoff_minutes",
      origins: ["explicit", "mention"],
      title: "킥오프 회의록 배포",
      createdAt: "2026-09-02T02:00:00.000Z",
      createdBy: "김PM",
      blockId: "erp_b5",
    }),
    docLink({
      id: "erp_dl_2",
      documentId: "erp_doc_kickoff",
      targetKind: "scheduleTask",
      targetId: "t1-1",
      origins: ["explicit"],
      title: "킥오프 미팅",
      createdAt: "2026-09-02T02:05:00.000Z",
      createdBy: "김PM",
    }),
    docLink({
      id: "erp_dl_3",
      documentId: "erp_doc_weekly",
      targetKind: "executionTask",
      targetId: "x_weekly",
      origins: ["mention"],
      title: "주간 진척 보고",
      createdAt: "2026-10-20T01:10:00.000Z",
      createdBy: "나",
      blockId: "erp_w2",
    }),
    docLink({
      id: "erp_dl_4",
      documentId: "erp_doc_weekly",
      targetKind: "record",
      targetId: "r_iss_mes",
      origins: ["explicit"],
      code: "ISS-2",
      title: "MES 인터페이스 사양 버전 불일치",
      createdAt: "2026-10-20T01:20:00.000Z",
      createdBy: "나",
      blockId: "erp_w4",
    }),
    docLink({
      id: "erp_dl_5",
      documentId: "erp_doc_if",
      targetKind: "executionTask",
      targetId: "x_if_map",
      origins: ["explicit"],
      title: "MES 인터페이스 필드 매핑",
      createdAt: "2026-10-18T08:20:00.000Z",
      createdBy: "나",
      blockId: "erp_i1",
    }),
  ];
}

export function sampleRobotDocuments(): PlanDocument[] {
  return [
    sampleDoc({
      id: "rob_doc_safety",
      seq: 1,
      title: "파일럿 셀 안전 인증 회의",
      kind: "meeting",
      visibility: "plan",
      authorId: "한로봇",
      heldAt: "2026-10-08",
      createdAt: "2026-10-08T02:00:00.000Z",
      updatedAt: "2026-10-08T07:00:00.000Z",
      blocks: [
        heading("rob_b1", "인증 범위"),
        paragraph("rob_b2", "파일럿 1셀을 양산 3셀보다 먼저 열고, 그 셀만 이번 안전 인증에 넣기로 했습니다."),
        bullet("rob_b3", "안전펜스 높이는 현장 실측이 도면과 맞는지 이번 주에 확인합니다."),
        paragraphMention("rob_b4", "인증 체크리스트 초안을 배포한다 ", "executionTask", "rob_x1", ""),
        checkItem("rob_b5", "비상 정지 후 재가동은 2인 확인으로 적는다", true),
      ],
      attachments: [fileAttachment("rob_doc_att_safety", "안전 인증 체크리스트 초안.pdf", "2026-10-08T03:00:00.000Z", "한로봇")],
      comments: [{ id: "rob_doc_cm1", author: "백제어", body: "라이트커튼 위치는 제어 시퀀스에 반영하겠습니다.", at: "2026-10-08T07:00:00.000Z" }],
    }),
    sampleDoc({
      id: "rob_doc_weekly",
      seq: 2,
      title: "파일럿 셀 주간 보고",
      kind: "memo",
      visibility: "plan",
      authorId: "나",
      createdAt: "2026-10-20T02:00:00.000Z",
      blocks: [
        heading("rob_w1", "이번 주"),
        paragraphMention("rob_w2", "셀 진척은 ", "executionTask", "rob_x_me_cell", "에서 보고, 암 납기는 별도 이슈로 둡니다."),
        bullet("rob_w3", "1호기 암이 계획보다 6일 늦습니다. 파일럿 1셀만 선행할지 견적과 함께 봅니다."),
        quoteBlock("rob_w4", "양산 3셀 일정은 인증이 끝날 때까지 움직이지 않습니다."),
      ],
    }),
    sampleDoc({
      id: "rob_doc_label",
      seq: 3,
      title: "불량 샘플 라벨 기준",
      kind: "material",
      visibility: "private",
      authorId: "나",
      createdAt: "2026-10-17T05:00:00.000Z",
      blocks: [
        paragraph("rob_l1", "스크래치, 찍힘, 치수 이탈 세 갈래만 1차 라벨로 둡니다. 애매한 경계 샘플은 나비전 확인 후에 넣습니다."),
      ],
      attachments: [imageAttachment("rob_doc_att_label", "불량 샘플 예.png", "2026-10-17T05:10:00.000Z", "나")],
    }),
  ];
}

export function sampleRobotDocumentLinks(): DocumentLink[] {
  return [
    docLink({
      id: "rob_dl_1",
      documentId: "rob_doc_safety",
      targetKind: "executionTask",
      targetId: "rob_x1",
      origins: ["explicit", "mention"],
      title: "파일럿 셀 안전 인증 체크리스트 초안",
      createdAt: "2026-10-08T02:30:00.000Z",
      createdBy: "한로봇",
      blockId: "rob_b4",
    }),
    docLink({
      id: "rob_dl_2",
      documentId: "rob_doc_safety",
      targetKind: "record",
      targetId: "rob_dec_pilot",
      origins: ["explicit"],
      code: "DEC-1",
      title: "파일럿 1셀을 양산 3셀보다 먼저 연다",
      createdAt: "2026-10-08T02:40:00.000Z",
      createdBy: "한로봇",
    }),
    docLink({
      id: "rob_dl_3",
      documentId: "rob_doc_weekly",
      targetKind: "executionTask",
      targetId: "rob_x_me_cell",
      origins: ["mention"],
      title: "파일럿 셀 진척 점검",
      createdAt: "2026-10-20T02:10:00.000Z",
      createdBy: "나",
      blockId: "rob_w2",
    }),
    docLink({
      id: "rob_dl_4",
      documentId: "rob_doc_weekly",
      targetKind: "record",
      targetId: "rob_iss_arm",
      origins: ["explicit"],
      code: "ISS-1",
      title: "1호기 로봇 암 납기 지연",
      createdAt: "2026-10-20T02:20:00.000Z",
      createdBy: "나",
    }),
    docLink({
      id: "rob_dl_5",
      documentId: "rob_doc_label",
      targetKind: "executionTask",
      targetId: "rob_x_vision_week",
      origins: ["explicit"],
      title: "불량 샘플 라벨 기준 합의",
      createdAt: "2026-10-17T05:20:00.000Z",
      createdBy: "나",
    }),
  ];
}
